import { useRef } from 'react';
import { Card, CardContent, Input } from '../../../components/ui';
import type { GalleryImage } from '../types/microsite.types';

interface GallerySectionProps {
    gallery?: GalleryImage[];
    onChange: (images: GalleryImage[]) => void;
}

export default function GallerySection({ gallery = [], onChange }: GallerySectionProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);

        // Validate files
        const validFiles = files.filter((file) => {
            if (!file.type.startsWith('image/')) {
                alert(`${file.name} is not an image file`);
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert(`${file.name} is larger than 5MB`);
                return false;
            }
            return true;
        });

        // Convert files to GalleryImage format
        const newImages: GalleryImage[] = validFiles.map((file) => ({
            file,
            altText: '',
        }));

        onChange([...gallery, ...newImages]);

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (index: number) => {
        onChange(gallery.filter((_, i) => i !== index));
    };

    const handleUpdateAltText = (index: number, altText: string) => {
        const updated = [...gallery];
        updated[index] = { ...updated[index], altText };
        onChange(updated);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8b5cf6] to-[#a78bfa] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">5</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">Gallery</h3>
                        <p className="text-sm text-[#64748b]">
                            Upload images to showcase your products and store ambiance
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-[#64748b]">
                            {gallery.length > 0
                                ? `${gallery.length} image${gallery.length === 1 ? '' : 's'} uploaded (max 20)`
                                : 'No images uploaded yet'}
                        </p>
                        {gallery.length < 20 && (
                            <button
                                type="button"
                                onClick={handleClick}
                                className="px-4 py-2 bg-[#8b5cf6] text-white rounded-lg hover:bg-[#7c3aed] transition-colors text-sm font-medium"
                            >
                                + Add Images ({gallery.length}/20)
                            </button>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* Images Grid */}
                    {gallery.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {gallery.map((image, index) => (
                                <div
                                    key={index}
                                    className="border border-[#e2e8f0] rounded-lg p-3 space-y-2"
                                >
                                    <div className="relative group">
                                        <img
                                            src={
                                                image.url ||
                                                (image.file ? URL.createObjectURL(image.file) : '')
                                            }
                                            alt={image.altText || `Gallery image ${index + 1}`}
                                            className="w-full h-32 object-cover rounded-lg border-2 border-[#e2e8f0]"
                                        />
                                        <div className="absolute top-2 right-2">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="p-1.5 bg-[#ef4444] text-white rounded-full hover:bg-[#dc2626] transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="absolute top-2 left-2">
                                            <span className="px-2 py-1 bg-[#1e293b] bg-opacity-70 text-white text-xs rounded font-medium">
                                                {index + 1}
                                            </span>
                                        </div>
                                    </div>

                                    <Input
                                        type="text"
                                        value={image.altText || ''}
                                        onChange={(e) => handleUpdateAltText(index, e.target.value)}
                                        placeholder="Image description"
                                        maxLength={100}
                                        className="text-sm"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {gallery.length === 0 && (
                        <button
                            type="button"
                            onClick={handleClick}
                            className="w-full h-32 border-2 border-dashed border-[#cbd5e1] rounded-lg hover:border-[#8b5cf6] hover:bg-[#faf5ff] transition-all flex flex-col items-center justify-center gap-2 group"
                        >
                            <svg
                                className="w-10 h-10 text-[#cbd5e1] group-hover:text-[#8b5cf6] transition-colors"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            <p className="text-sm text-[#64748b] group-hover:text-[#8b5cf6] font-medium">
                                Upload Gallery Images
                            </p>
                            <p className="text-xs text-[#94a3b8]">
                                Select multiple files • Max 5MB each • Up to 20 images
                            </p>
                        </button>
                    )}

                    <p className="text-xs text-[#64748b]">
                        Upload up to 20 images for your gallery. Add descriptive alt text for SEO and
                        accessibility.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
